import {useMutation, useQuery} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {Empty, InputNumber, Select, Spin, Switch, Table, Tooltip} from 'antd';
import {DeleteOutlined, InfoCircleOutlined} from '@ant-design/icons';
import {useState} from 'react';
import {toast} from 'react-toastify';
import {
  GET_ASSET_CLASS_INSTRUMENTS,
  LIST_MANAGED_FUND_INVENTORY_CONFIGS,
} from 'graphql/queries/assetManagement';
import {BULK_UPSERT_MANAGED_FUND_INVENTORY_CONFIGS} from 'graphql/mutations/assetManagement';
import styles from './managedFundConfigs.module.scss';

export const ManagedFundConfigs = ({
  open,
  assetClassKey,
  canManage = true,
  onClose,
}) => {
  const [items, setItems] = useState([]);
  const [selectedFund, setSelectedFund] = useState(null);

  const {loading: configsLoading, refetch} = useQuery(
    LIST_MANAGED_FUND_INVENTORY_CONFIGS,
    {
      variables: {},
      skip: !open,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
      onCompleted: res => {
        const result = res?.listManagedFundInventoryConfigs;
        if (result?.__typename === 'Error') {
          toast.error(result.message);
          return;
        }
        setItems(
          (result?.data || []).map(config => ({
            ticker: config.ticker,
            weeklyDivisor: config.weeklyDivisor ?? null,
            bufferRate: config.bufferRate ?? null,
            active: config.active ?? true,
          })),
        );
      },
    },
  );

  const {data: fundsData, loading: fundsLoading} = useQuery(
    GET_ASSET_CLASS_INSTRUMENTS,
    {
      variables: {
        input: {
          explore: false,
          instrumentKey: 'MUTUAL_FUND',
          assetClassKey,
          paginate: false,
          groups: false,
        },
      },
      skip: !open || !assetClassKey,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [bulkUpsert, {loading: saving}] = useMutation(
    BULK_UPSERT_MANAGED_FUND_INVENTORY_CONFIGS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const funds = (fundsData?.getFinancialInstruments?.data || []).filter(
    fund => fund?.ticker,
  );
  const fundByTicker = funds.reduce((acc, fund) => {
    acc[fund.ticker] = fund;
    return acc;
  }, {});

  const configuredTickers = items.map(item => item.ticker);
  const fundOptions = funds
    .filter(fund => !configuredTickers.includes(fund.ticker))
    .map(fund => ({
      label: `${fund.name || fund.ticker} • ${fund.ticker}`,
      value: fund.ticker,
    }));

  const addFund = ticker => {
    if (!ticker || configuredTickers.includes(ticker)) return;
    setItems(prev => [
      ...prev,
      {ticker, weeklyDivisor: null, bufferRate: null, active: true},
    ]);
    setSelectedFund(null);
  };

  const updateItem = (ticker, patch) => {
    setItems(prev =>
      prev.map(item => (item.ticker === ticker ? {...item, ...patch} : item)),
    );
  };

  const removeItem = ticker => {
    setItems(prev => prev.filter(item => item.ticker !== ticker));
  };

  const handleSave = () => {
    // Only send fields that actually have a value — the backend rejects
    // null numbers ("Buffer Rate must be a number"), so omit blanks entirely.
    const configs = items.map(item => {
      const config = {ticker: item.ticker};
      if (typeof item.weeklyDivisor === 'number') {
        config.weeklyDivisor = item.weeklyDivisor;
      }
      if (typeof item.bufferRate === 'number') {
        config.bufferRate = item.bufferRate;
      }
      if (typeof item.active === 'boolean') {
        config.active = item.active;
      }
      return config;
    });

    bulkUpsert({variables: {input: {configs}}})
      .then(({data}) => {
        const result = data?.bulkUpsertManagedFundInventoryConfigs;
        if (!result || result.__typename === 'Error') {
          toast.error(result?.message || 'Failed to save configurations');
          return;
        }
        toast.success(result.message || 'Configurations saved');
        refetch();
        onClose?.();
      })
      .catch(error => toast.error(error.message));
  };

  const columns = [
    {
      title: 'Fund',
      dataIndex: 'ticker',
      key: 'ticker',
      render: (ticker, record) => (
        <div className={styles.fundCell}>
          <span>{fundByTicker[ticker]?.name || ticker}</span>
          <small>{record.ticker}</small>
        </div>
      ),
    },
    {
      title: (
        <span className={styles.thTip}>
          Weekly Divisor
          <Tooltip title="Divides recent trade volume to estimate weekly average demand. Leave blank to use the global default.">
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      ),
      dataIndex: 'weeklyDivisor',
      key: 'weeklyDivisor',
      render: (value, record) => (
        <InputNumber
          value={value}
          min={0}
          step={1}
          placeholder="e.g. 4"
          disabled={!canManage}
          className={styles.numberInput}
          onChange={val => updateItem(record.ticker, {weeklyDivisor: val})}
        />
      ),
    },
    {
      title: (
        <span className={styles.thTip}>
          Buffer Rate
          <Tooltip title="Safety-stock fraction added on top of predicted demand (e.g. 0.1 = 10%). Leave blank to use the global default.">
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      ),
      dataIndex: 'bufferRate',
      key: 'bufferRate',
      render: (value, record) => (
        <InputNumber
          value={value}
          min={0}
          step={0.05}
          placeholder="e.g. 0.1"
          disabled={!canManage}
          className={styles.numberInput}
          onChange={val => updateItem(record.ticker, {bufferRate: val})}
        />
      ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (value, record) => (
        <Switch
          checked={value}
          disabled={!canManage}
          onChange={checked => updateItem(record.ticker, {active: checked})}
        />
      ),
    },
    {
      title: '',
      key: 'remove',
      render: (_, record) => (
        <Tooltip title="Remove — the fund is deleted from configs on save.">
          <DeleteOutlined
            className={styles.removeIcon}
            onClick={() => canManage && removeItem(record.ticker)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2>Managed Fund Configurations</h2>
        <p>
          Select the mutual funds that should be included in inventory
          predictions and tune their per-fund parameters. Removing a fund and
          saving deletes its configuration.
        </p>
      </div>

      {canManage && (
        <div className={styles.addRow}>
          <Select
            showSearch
            allowClear
            className={styles.fundSelect}
            placeholder="Add a fund…"
            value={selectedFund}
            loading={fundsLoading}
            options={fundOptions}
            filterOption={(input, option) =>
              (option?.label || '').toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={
              fundsLoading ? <Spin size="small" /> : 'No funds available'
            }
            onChange={value => {
              setSelectedFund(value);
              addFund(value);
            }}
          />
          <span className={styles.count}>
            {items.length} fund{items.length === 1 ? '' : 's'} configured
          </span>
        </div>
      )}

      <Table
        loading={configsLoading}
        dataSource={items}
        rowKey="ticker"
        pagination={false}
        className="antd-table"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No funds configured yet"
            />
          ),
        }}
        columns={columns}
      />

      <div className={styles.footer}>
        <Button type="secondary" label="Cancel" onClick={onClose} />
        <Button
          disabled={!canManage || saving || configsLoading}
          label={saving ? 'Saving…' : 'Save Configurations'}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};
